/**
 * /downhole/assemblies/:id/teardown — the office's teardown of a returned
 * string (fieldiq-api#905 `POST /assemblies/:id/disassemble`).
 *
 * THE SAME CHECKLIST AS THE YARD'S. The form is `TeardownChecklist` from the
 * field shell (field/FieldTeardownScreen.tsx), wrapped here in the office page;
 * every rule it enforces — one line per returned tool, no default condition or
 * action, return to stock only behind dope & cleaning, OD typed in the
 * tenant's units and sent in inches — is written there once. Only the links
 * differ: back to the builder, and each work order the teardown opens goes to
 * the office Work Orders screen, where it is assigned.
 *
 * Every build role may tear down (the API's DH_BUILD); a laid_down assembly
 * only. Reached from the builder's Tear down button, which shows on exactly
 * that status for exactly those roles.
 *
 * The answer is held HERE and checked before the status: the save re-reads
 * the assembly, which comes back `disassembled` (FieldTeardownScreen's
 * docblock has the whole of it).
 */
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useDownholeAssembly } from '../../hooks/useDownhole';
import { DH_ASSEMBLY_STATUS_SKIN, dhSkin } from '../../lib/downhole-labels';
import { canBuildDownhole } from '../../lib/downhole-roles';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { DhEmpty } from './DownholeBits';
import type { DhDisassemblyResult } from '../../types/downhole';
import { TeardownChecklist, TeardownResult, type TeardownLinks } from './field/FieldTeardownScreen';

export default function TeardownScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const query = useDownholeAssembly(id);
  const failure = queryLoadFailure(query);
  const asm = query.data?.data ?? null;
  const units = query.data?.meta.unit_system ?? null;
  const [result, setResult] = useState<DhDisassemblyResult | null>(null);
  const links: TeardownLinks = {
    back: `/downhole/assemblies/${id}/build`,
    backLabel: 'Back to the assembly',
    workOrder: (woId) => `/downhole/work-orders/${woId}`,
    done: `/downhole/assemblies/${id}/build`,
    doneLabel: 'Back to the assembly',
  };

  const notice = (children: React.ReactNode) => (
    <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">{children}</div>
  );

  return (
    <div data-testid="downhole-teardown-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={asm ? `Tear down · ${asm.assembly_number}` : 'Tear down'}
        title={asm?.name ?? (query.isLoading ? 'Loading…' : 'Assembly')}
        onBack={() => navigate(-1)}
        backLabel="Back"
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {result && asm ? (
            <TeardownResult result={result} assemblyNumber={asm.assembly_number} links={links} />
          ) : query.isLoading || (!failure && !query.data) ? (
            <div className="flex justify-center py-16">
              <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading assembly" />
            </div>
          ) : failure ? (
            notice(<LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="this assembly" />)
          ) : !asm ? (
            notice(<DhEmpty>This assembly does not exist in your register.</DhEmpty>)
          ) : !canBuildDownhole(user?.role) ? (
            notice(<DhEmpty>Your role cannot tear down an assembly.</DhEmpty>)
          ) : asm.status !== 'laid_down' ? (
            notice(
              <DhEmpty>
                {asm.assembly_number} is {dhSkin(DH_ASSEMBLY_STATUS_SKIN, asm.status).label.toLowerCase()}. Only a string back from the well
                can be torn down.
              </DhEmpty>,
            )
          ) : (
            <TeardownChecklist
              asm={asm}
              units={units}
              wide
              links={links}
              onDone={setResult}
              header={
                <p className="text-sm text-fieldiq-text">
                  One line per tool. Every tool needs a condition and what happens to it next. The work orders it opens start
                  unassigned; assign them on Work Orders.
                </p>
              }
            />
          )}
        </div>
      </section>
    </div>
  );
}
